import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cx } from "./cx.js";
import { Tooltip } from "./Tooltip.js";
import "../../styles/ui-refinement.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  /** Place directional icons after the label; leading icons are the default. */
  iconPosition?: "start" | "end";
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon: Icon, iconPosition = "start", loading = false, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx("btn", `btn--${variant}`, size === "sm" && "btn--sm", className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {Icon && iconPosition === "start" ? <Icon className="btn__icon" aria-hidden /> : null}
      {children}
      {Icon && iconPosition === "end" ? <Icon className="btn__icon" aria-hidden /> : null}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, "children" | "icon"> {
  icon: LucideIcon;
  /** Required: icon-only buttons need an accessible name. */
  label: string;
  /** Defaults to the accessible label. Pass false when another composite supplies help. */
  tooltip?: ReactNode | false;
  tooltipSide?: "top" | "right" | "bottom" | "left";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, tooltip, tooltipSide, className, variant = "ghost", title, "aria-label": ariaLabel, ...rest },
  ref,
) {
  const tooltipContent = tooltip === undefined ? title ?? label : tooltip;
  const button = (
    <Button
      ref={ref}
      variant={variant}
      className={cx("btn--icon", className)}
      aria-label={ariaLabel ?? label}
      {...rest}
    >
      <Icon className="btn__icon" aria-hidden />
    </Button>
  );
  return tooltipContent === false ? button : <Tooltip content={tooltipContent} side={tooltipSide}>{button}</Tooltip>;
});
